// web-screen-budget הכרעה 8: Esc בשכבות — מחסנית אחת. כל שכבה (הגדלה, sheet, drawer) נרשמת כשהיא נפתחת; מאזין Esc אחד
// ב-document סוגר את האחרונה בלבד: הגדלה + ?consent= → Esc סוגר את ה-drawer, Esc שני את ההגדלה, Esc שלישי — כלום (לא עוזב
// עמוד ולא נוגע בשדה הכתיבה). Esc שכבר טופל בפנים (preventDefault — רשימה נפתחת, לוח סינון של טבלה) לא סוגר שכבה.
// ההגדלה ב-packages/display נרשמת דרך LayerContext (שהאפליקציה מספקת ב-main.tsx) — בלי תלות של display באפליקציה.
import { useEffect, useRef } from 'react';
import type { LayerStack } from '@aibify/display/client';

type KeyTarget = { addEventListener(type: 'keydown', fn: (e: KeyboardEvent) => void): void };
type Esc = Pick<KeyboardEvent, 'key' | 'defaultPrevented' | 'isComposing' | 'preventDefault'>;

export function createLayerStack(target: () => KeyTarget | null) {
  const stack: Array<{ close: () => void }> = [];
  let listening = false;
  const onKey = (e: Esc) => {
    if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
    const top = stack.at(-1);
    if (!top) return;
    e.preventDefault();
    top.close();
  };
  const layers: LayerStack & { depth(): number; onKey(e: Esc): void } = {
    push(close) {
      const entry = { close };
      stack.push(entry);
      const t = listening ? null : target();
      if (t) {
        t.addEventListener('keydown', onKey);
        listening = true;
      }
      return () => {
        const i = stack.indexOf(entry);
        if (i >= 0) stack.splice(i, 1);
      };
    },
    depth: () => stack.length,
    onKey,
  };
  return layers;
}

/** המחסנית של האפליקציה (מאזין אחד על document). */
export const layers = createLayerStack(() => (typeof document === 'undefined' ? null : document));

/** שכבה פתוחה נרשמת בראש המחסנית; close העדכני נקרא (בלי רישום מחדש כשהפונקציה מתחלפת — הסדר נשמר). */
export function useLayer(open: boolean, close: () => void) {
  const ref = useRef(close);
  ref.current = close;
  useEffect(() => {
    if (!open) return;
    return layers.push(() => ref.current());
  }, [open]);
}
